import { type Coords } from '../map.type'

export const DEFAULT_MAX_ESTIMATED_TILES = 7000
export const MAX_OVERLAY_SAFE_PIXELS = 36_000_000

const TILE_SIZE = 256
const MIN_ZOOM = 8
const EXTRA_ZOOM_COUNT = 2
const MAX_SAFE_ZOOM = 22

export type TileEstimationResult = {
  totalTiles: number
  naturalMaxZoom: number
  maxZoom: number
  isExceedingThreshold: boolean
}

/**
 * Calculates Web Mercator tile X coordinate for a given longitude and zoom level.
 */
function longToTile(lng: number, zoom: number): number {
  const scale = 1 << zoom
  return (0.5 + lng / 360) * scale
}

/**
 * Calculates Web Mercator tile Y coordinate for a given latitude and zoom level.
 */
function latToTile(lat: number, zoom: number): number {
  const scale = 1 << zoom
  let siny = Math.sin((lat * Math.PI) / 180)
  siny = Math.min(Math.max(siny, -0.9999), 0.9999)
  return (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)) * scale
}

/**
 * Predicts the total number of tiles generated by the backend tiler
 * based on geographic corners and source image dimensions.
 */
export function estimateTileCount(
  corners: Coords[] | null | undefined,
  dimensions: { width: number; height: number } | null | undefined,
  threshold = DEFAULT_MAX_ESTIMATED_TILES,
): TileEstimationResult {
  if (!corners || corners.length < 4 || !dimensions) {
    return {
      totalTiles: 0,
      naturalMaxZoom: MIN_ZOOM,
      maxZoom: MIN_ZOOM,
      isExceedingThreshold: false,
    }
  }

  const { width: widthPx, height: heightPx } = dimensions
  if (widthPx <= 0 || heightPx <= 0) {
    return {
      totalTiles: 0,
      naturalMaxZoom: MIN_ZOOM,
      maxZoom: MIN_ZOOM,
      isExceedingThreshold: false,
    }
  }

  const lats = corners.map((c) => Number(c.lat)).filter((n) => !isNaN(n))
  const lngs = corners.map((c) => Number(c.lng)).filter((n) => !isNaN(n))

  if (lats.length < 4 || lngs.length < 4) {
    return {
      totalTiles: 0,
      naturalMaxZoom: MIN_ZOOM,
      maxZoom: MIN_ZOOM,
      isExceedingThreshold: false,
    }
  }

  const northBound = Math.max(...lats)
  const southBound = Math.min(...lats)
  const westBound = Math.min(...lngs)
  const eastBound = Math.max(...lngs)

  if (northBound === southBound && westBound === eastBound) {
    return {
      totalTiles: 1,
      naturalMaxZoom: MIN_ZOOM,
      maxZoom: MIN_ZOOM + EXTRA_ZOOM_COUNT,
      isExceedingThreshold: threshold < 1,
    }
  }

  let totalTiles = 0
  let z = MIN_ZOOM - 1
  let naturalMaxZoom = -1

  while (z < MAX_SAFE_ZOOM) {
    z++
    const xWest = longToTile(westBound, z)
    const yNorth = latToTile(northBound, z)
    const xEast = longToTile(eastBound, z)
    const ySouth = latToTile(southBound, z)

    const xFlooredWest = Math.floor(xWest)
    const yFlooredNorth = Math.floor(yNorth)
    const xFlooredEast = Math.floor(xEast)
    const yFlooredSouth = Math.floor(ySouth)

    const widthTc = Math.max(xEast - xWest, 0.0001)
    const heightTc = Math.max(ySouth - yNorth, 0.0001)

    const srcTileWidth = widthPx / widthTc
    const srcTileHeight = heightPx / heightTc

    if (
      (srcTileWidth <= TILE_SIZE || srcTileHeight <= TILE_SIZE) &&
      naturalMaxZoom < 0
    ) {
      naturalMaxZoom = z
    }

    if (naturalMaxZoom >= 0 && z > naturalMaxZoom + EXTRA_ZOOM_COUNT) {
      z--
      break
    }

    const tileCount =
      (xFlooredEast - xFlooredWest + 1) * (yFlooredSouth - yFlooredNorth + 1)
    totalTiles += Math.max(0, tileCount)
  }

  const resolvedNaturalZoom = naturalMaxZoom >= 0 ? naturalMaxZoom : z
  const maxZoom = resolvedNaturalZoom + EXTRA_ZOOM_COUNT

  return {
    totalTiles,
    naturalMaxZoom: resolvedNaturalZoom,
    maxZoom,
    isExceedingThreshold: totalTiles > threshold,
  }
}

/**
 * Loads an image by URL and resolves its natural dimensions.
 */
export async function getImageDimensions(
  url: string,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      })
    }
    img.onerror = () => {
      resolve({ width: 500, height: 500 })
    }
    img.src = url
    img.crossOrigin = 'Anonymous'
  })
}

/**
 * Extracts natural dimensions directly from a local File instance before upload.
 */
export async function getImageFileDimensions(
  file: File,
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      const dimensions = {
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height,
      }
      URL.revokeObjectURL(objectUrl)
      resolve(dimensions)
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Failed to load image file dimensions'))
    }
    img.src = objectUrl
  })
}

/**
 * Checks whether an image resolution exceeds the safe overlay pixel threshold.
 */
export function isImageOversized(
  dimensions: { width: number; height: number },
  maxPixels = MAX_OVERLAY_SAFE_PIXELS,
): boolean {
  return dimensions.width * dimensions.height > maxPixels
}
